import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Reveal from "../components/Reveal";

const TRACKS = [
  {
    name: "Website Build",
    desc: "A custom-designed site built for your business. Homepage, services, portfolio, contact. Mobile-optimized. Deployed in the session.",
    price: "$3,000",
  },
  {
    name: "Business Tool Build",
    desc: "A custom AI-powered tool for your business — intake system, client portal, booking flow, proposal generator, or dashboard. Built to work, not to demo.",
    price: "$3,500",
  },
  {
    name: "Combined Build",
    desc: "Full site + embedded business tool in one session. The complete package — save $1,500 versus booking the two separately.",
    price: "$5,000",
    featured: true,
  },
];

const INCLUDED = [
  "Discovery call before we book",
  "One dedicated build session (half-day or full-day by scope)",
  "Full deployment and handover",
  "30-day minor revision window",
  "You own everything — no platform lock-in",
  "Direct access to Jeff throughout the build",
];

const NOT = {
  left: ["Not a template", "Not Wix or Squarespace", "Not outsourced"],
  right: [
    "Not a 6-week project",
    "Not an ongoing retainer (unless you want one — ask)",
    "Not AI-generated slop with your logo dropped on top",
  ],
};

const FAQS = [
  {
    q: "How long does a build session actually take?",
    a: "Half-day sessions run 3–4 hours. Full-day sessions run 6–8 hours. Scope determines which we need — I'll tell you before we book.",
  },
  {
    q: "What do I need to prepare?",
    a: "Your brand assets if you have them, any copy you want to use, and a clear idea of what you need. I'll walk you through the rest on our discovery call.",
  },
  {
    q: "What if I need changes after?",
    a: "The 30-day revision window covers minor updates. Anything significant is scoped and quoted separately — no surprises.",
  },
  {
    q: "Do you do ongoing work?",
    a: "Sometimes. If a project makes sense as an ongoing engagement, we can talk about it. But my default is a clean handover — you run it, you own it.",
  },
];

function FAQ({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="hairline-b" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full py-6 md:py-7 flex items-center justify-between gap-6 text-left"
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="display-h3 pr-4">{q}</span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-muted transition-transform duration-300 ${open ? "rotate-180 text-teal" : ""}`}
        />
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{ maxHeight: open ? 240 : 0, opacity: open ? 1 : 0 }}
      >
        <p className="pb-7 text-lg text-ink/80 leading-relaxed max-w-3xl">{a}</p>
      </div>
    </div>
  );
}

export default function Build() {
  useEffect(() => {
    document.title = "The Build — Socialize Video";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-bg" data-testid="build-page">
      {/* HEADER */}
      <section className="pt-20 md:pt-28 pb-16 md:pb-20" data-testid="build-header">
        <div className="container-x grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-9 lg:col-span-9">
            <div className="eyebrow mb-6">The Build</div>
            <h1 className="display-hero mb-7">What I Build — and What It Costs</h1>
            <p className="text-xl text-ink/80 max-w-2xl leading-relaxed">
              Custom websites and AI-powered business tools, built fast, built right.
            </p>
          </div>
        </div>
      </section>

      {/* TRACKS */}
      <section className="pb-24" data-testid="tracks-section">
        <div className="container-x">
          <Reveal staggerChildrenMs={90} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TRACKS.map((t, i) => (
              <div
                key={t.name}
                className={`reveal bg-surface hairline border rounded-lg p-7 md:p-8 flex flex-col ${t.featured ? "ring-1 ring-teal/30" : ""}`}
                data-testid={`track-card-${i + 1}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="eyebrow">{`Track 0${i + 1}`}</span>
                  {t.featured && <span className="text-xs uppercase tracking-widest text-teal">Most complete</span>}
                </div>
                <h3 className="display-h3 mb-4">{t.name}</h3>
                <p className="text-muted leading-relaxed flex-1">{t.desc}</p>
                <div className="mt-8 pt-6 hairline-t flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-widest text-muted">Starting at</span>
                  <span className="font-display text-2xl md:text-3xl font-bold text-teal">{t.price}</span>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="py-24 md:py-28 bg-surface" data-testid="included-section">
        <div className="container-x grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-5">Always included</div>
            <Reveal as="h2" className="display-h2">What's always included</Reveal>
          </div>
          <div className="col-span-12 md:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
              {INCLUDED.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-ink/90 leading-relaxed" data-testid={`included-${i + 1}`}>
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-teal shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT THIS IS NOT */}
      <section className="py-24 md:py-28" data-testid="not-section">
        <div className="container-x grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-5">What this isn't</div>
            <Reveal as="h2" className="display-h2">A few things this is not</Reveal>
          </div>
          <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <ul className="space-y-3 text-muted">
              {NOT.left.map((x, i) => (
                <li key={i} className="text-lg" data-testid={`not-left-${i + 1}`}>— {x}</li>
              ))}
            </ul>
            <ul className="space-y-3 text-muted">
              {NOT.right.map((x, i) => (
                <li key={i} className="text-lg" data-testid={`not-right-${i + 1}`}>— {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOUNDER RATE — dark insert with teal left border */}
      <section className="pb-24" data-testid="founder-rate-section">
        <div className="container-x">
          <Reveal className="section-dark rounded-r-lg pl-7 md:pl-10 pr-7 md:pr-10 py-10 md:py-12 border-l-2 border-teal max-w-3xl">
            <div className="eyebrow text-teal mb-4">Founder Rate Builds</div>
            <h3 className="display-h3 mb-5">Reduced rate, in exchange for a public case study.</h3>
            <p className="text-ink/80 leading-relaxed mb-7">
              Occasionally I open 1–2 spots for early-stage founders whose business is a strong candidate for a public case study. These builds are offered at a reduced rate in exchange for permission to document the process publicly.
            </p>
            <p className="text-muted mb-7">If you think that's you, mention it in your application.</p>
            <Link to="/apply" className="cta" data-testid="founder-apply-cta">
              Apply and mention it <span aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-28 bg-surface" data-testid="faq-section">
        <div className="container-x grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-5">FAQ</div>
            <Reveal as="h2" className="display-h2">A few honest answers.</Reveal>
          </div>
          <div className="col-span-12 md:col-span-8">
            {FAQS.map((f, i) => (
              <FAQ key={f.q} index={i + 1} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
